import type { GameView, tarneeb } from "@app/rules";
import type { ToastData } from "../components/Toast";
import type { BiddingView } from "../variants/types";
import type { T } from "./tableText";
import { type Seats, signed, teamsLine } from "./toastParts";

/** Batak and Tarneeb in words: both bid for a contract and name a trump. */

/** The bid side of a seat's line: the contract, a pass, or the standing high bid. */
function bidPart(view: BiddingView, seat: number, t: T): string | null {
  if (view.contract?.seat === seat) return t("table.seat.bid", { amount: view.contract.amount });
  if (view.bidding?.passed[seat]) return t("table.seat.pass");
  if (view.bidding?.highest?.seat === seat)
    return t("table.seat.bid", { amount: view.bidding.highest.amount });
  return null;
}

/** Your own line while the bid or the trump is yours (design 4, 5). */
function yourBiddingText(view: BiddingView, t: T): string {
  return view.phase === "bidding"
    ? t("table.bid.you")
    : t("table.seat.chooseTrump", { amount: view.contract?.amount ?? 0 });
}

/** Batak and Tarneeb: your bid or trump when it is yours, "thinking" for others, else bid and tricks. */
export function biddingSeatSub(
  view: BiddingView,
  seat: number,
  yourSeat: number | null,
  t: T,
): string {
  const deciding = view.turn === seat && (view.phase === "bidding" || view.phase === "trump");
  if (deciding) return seat === yourSeat ? yourBiddingText(view, t) : t("table.seat.thinking");
  const parts = [bidPart(view, seat, t)];
  if (view.contract !== null)
    parts.push(t("table.seat.tricks", { tricks: view.tricksWon[seat] ?? 0 }));
  const line = parts.filter((p) => p !== null).join(" · ");
  return line === "" ? t("table.seat.waiting") : line;
}

/** Batak: "Ahmet bid 6 and made it / +60 points" (design 7). */
export function batakHandEnd(view: GameView, seats: Seats, t: T): ToastData | null {
  const result = view.lastHandResult;
  if (result === null) return null;
  const { seat, amount } = result.contract;
  const points = signed(result.deltas[seat] ?? 0);
  return {
    title: t(result.made ? "table.handEnd.made" : "table.handEnd.failed", {
      name: seats[seat]?.name ?? "?",
      amount,
    }),
    sub: t(result.made ? "table.toast.points" : "table.toast.down", { points }),
    tone: result.made ? "success" : "error",
    member: seats[seat] ?? null,
  };
}

/** Tarneeb: who bid what and whether the team made it, then the team deltas. */
export function tarneebHandEnd(view: tarneeb.TarneebView, seats: Seats, t: T): ToastData | null {
  const result = view.lastHandResult;
  if (result === null) return null;
  const { seat, amount } = result.contract;
  return {
    title: t(result.made ? "table.handEnd.made" : "table.handEnd.failed", {
      name: seats[seat]?.name ?? "?",
      amount,
    }),
    sub: teamsLine(result.deltas, view.yourSeat, t),
    tone: result.made ? "success" : "error",
    member: seats[seat] ?? null,
  };
}

/** You won the bid and pick the trump now (design 5). */
export function trumpPickToast(view: BiddingView, yourSeat: number | null, t: T): ToastData | null {
  if (view.phase !== "trump") return null;
  if (view.contract === null || view.contract.seat !== yourSeat) return null;
  // Everyone passed and the opener plays 4: you did not win anything (Batak, no re-deal).
  const forced = "forced" in view.contract && view.contract.forced === true;
  const amount = view.contract.amount;
  return {
    title: forced ? t("table.toast.forced", { amount }) : t("table.toast.won", { amount }),
    sub: t("table.toast.pickTrump"),
    tone: "warning",
  };
}
